/**
 * Periodo do painel (RNF15).
 *
 * O filtro de data e o controle que todo leitor procura primeiro, e ele
 * escopa TUDO abaixo dele: KPIs, serie, funil, leads e campanhas leem a mesma
 * janela, entao os numeros sempre batem entre si.
 *
 * Decisoes:
 *
 *  1. A janela viaja como INSTANTES (ISO-8601), `from` inclusivo e `to`
 *     exclusivo. "Ultimos 30 dias" e resolvido AQUI, no fuso do navegador do
 *     lojista: meia-noite local de 30 dias atras ate a meia-noite local de
 *     amanha. O servidor nao adivinha fuso.
 *
 *  2. O periodo mora na URL (`?periodo=30d` ou `?de=2026-09-01&ate=2026-09-24`)
 *     e so por ultimo no localStorage. Link compartilhado abre na mesma janela;
 *     a memoria local vale so quando a URL nao diz nada.
 *
 *  3. `tudo` existe porque o produto acaba de nascer: com dez dias de dado, um
 *     filtro de 30 dias e igual a "tudo", e o lojista precisa poder ver o total
 *     acumulado sem calcular data.
 */

import type { PeriodRange } from '../types';

export type PeriodPreset = 'hoje' | '7d' | '30d' | '90d' | '6m' | '12m' | 'tudo';

export type PeriodSelection =
  | { readonly kind: 'preset'; readonly preset: PeriodPreset }
  | { readonly kind: 'custom'; readonly from: string; readonly to: string }; // YYYY-MM-DD, `to` inclusivo

export const PRESETS: readonly { readonly value: PeriodPreset; readonly label: string; readonly hint: string }[] = [
  { value: 'hoje', label: 'Hoje', hint: 'Desde a meia-noite' },
  { value: '7d', label: 'Últimos 7 dias', hint: 'Inclui hoje' },
  { value: '30d', label: 'Últimos 30 dias', hint: 'Inclui hoje' },
  { value: '90d', label: 'Últimos 90 dias', hint: 'Inclui hoje' },
  { value: '6m', label: 'Últimos 6 meses', hint: '182 dias, inclui hoje' },
  { value: '12m', label: 'Últimos 12 meses', hint: '365 dias, inclui hoje' },
  { value: 'tudo', label: 'Desde o início', hint: 'Sem filtro de data' },
];

export const DEFAULT_SELECTION: PeriodSelection = { kind: 'preset', preset: '30d' };

const STORAGE_KEY = 'cf.periodo';
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function isPreset(value: string): value is PeriodPreset {
  return PRESETS.some((p) => p.value === value);
}

/** Meia-noite LOCAL de um YYYY-MM-DD. `new Date('2026-09-01')` seria UTC. */
function localMidnight(dateOnly: string): Date | null {
  if (!DATE_ONLY.test(dateOnly)) {
    return null;
  }
  const [y, m, d] = dateOnly.split('-').map(Number);
  const date = new Date(y ?? 0, (m ?? 1) - 1, d ?? 1);
  return Number.isNaN(date.getTime()) ? null : date;
}

function addDays(date: Date, days: number): Date {
  const out = new Date(date);
  out.setDate(out.getDate() + days);
  return out;
}

function startOfToday(now: Date): Date {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export function toDateOnly(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Resolve a selecao em instantes. `tudo` devolve nulos. */
export function resolveRange(selection: PeriodSelection, now: Date = new Date()): PeriodRange {
  if (selection.kind === 'custom') {
    const from = localMidnight(selection.from);
    const toInclusive = localMidnight(selection.to);
    if (from === null || toInclusive === null) {
      return resolveRange(DEFAULT_SELECTION, now);
    }
    return { from: from.toISOString(), to: addDays(toInclusive, 1).toISOString() };
  }

  const tomorrow = addDays(startOfToday(now), 1);
  switch (selection.preset) {
    case 'hoje':
      return { from: startOfToday(now).toISOString(), to: tomorrow.toISOString() };
    case '7d':
      return { from: addDays(tomorrow, -7).toISOString(), to: tomorrow.toISOString() };
    case '30d':
      return { from: addDays(tomorrow, -30).toISOString(), to: tomorrow.toISOString() };
    case '90d':
      return { from: addDays(tomorrow, -90).toISOString(), to: tomorrow.toISOString() };
    case '6m':
      return { from: addDays(tomorrow, -182).toISOString(), to: tomorrow.toISOString() };
    case '12m':
      return { from: addDays(tomorrow, -365).toISOString(), to: tomorrow.toISOString() };
    case 'tudo':
      return { from: null, to: null };
  }
}

/** Numero de dias cobertos, para decidir o formato dos rotulos do grafico. */
export function rangeDays(range: PeriodRange): number | null {
  if (range.from === null || range.to === null) {
    return null;
  }
  return Math.round((new Date(range.to).getTime() - new Date(range.from).getTime()) / 86_400_000);
}

export function describeSelection(selection: PeriodSelection): string {
  if (selection.kind === 'preset') {
    return PRESETS.find((p) => p.value === selection.preset)?.label ?? selection.preset;
  }
  const fmt = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short' });
  const from = localMidnight(selection.from);
  const to = localMidnight(selection.to);
  if (from === null || to === null) {
    return 'Período personalizado';
  }
  return `${fmt.format(from)} – ${fmt.format(to)}`;
}

// -----------------------------------------------------------------------------
// URL e memoria local
// -----------------------------------------------------------------------------

export function readSelectionFromSearch(search: string): PeriodSelection | null {
  const params = new URLSearchParams(search);
  const preset = params.get('periodo');
  if (preset !== null && isPreset(preset)) {
    return { kind: 'preset', preset };
  }
  const de = params.get('de');
  const ate = params.get('ate');
  if (de !== null && ate !== null && DATE_ONLY.test(de) && DATE_ONLY.test(ate) && de <= ate) {
    return { kind: 'custom', from: de, to: ate };
  }
  return null;
}

export function writeSelectionToSearch(search: string, selection: PeriodSelection): string {
  const params = new URLSearchParams(search);
  params.delete('periodo');
  params.delete('de');
  params.delete('ate');
  if (selection.kind === 'preset') {
    params.set('periodo', selection.preset);
  } else {
    params.set('de', selection.from);
    params.set('ate', selection.to);
  }
  const text = params.toString();
  return text === '' ? '' : `?${text}`;
}

export function readStoredSelection(): PeriodSelection | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }
    const record = parsed as Record<string, unknown>;
    if (record['kind'] === 'preset' && typeof record['preset'] === 'string' && isPreset(record['preset'])) {
      return { kind: 'preset', preset: record['preset'] };
    }
    if (
      record['kind'] === 'custom' &&
      typeof record['from'] === 'string' &&
      typeof record['to'] === 'string' &&
      DATE_ONLY.test(record['from']) &&
      DATE_ONLY.test(record['to'])
    ) {
      return { kind: 'custom', from: record['from'], to: record['to'] };
    }
    return null;
  } catch {
    return null;
  }
}

export function storeSelection(selection: PeriodSelection): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
  } catch {
    // Armazenamento indisponivel (modo privado) nao derruba o painel.
  }
}
